/**
 * Helpers for shifts that occupy more than one row per date.
 *
 * A 24/7 booking stores TWO rows against one date — its two blocks — grouped by
 * a shared `blockDate`. Every other shift stores one. Moving a date between the
 * two shapes is where duplicate rows come from.
 */

interface BlockRowLike {
    date?: string | undefined;
    saved?: boolean | undefined;
    shiftName?: string | undefined;
    blockDate?: string | undefined | null;
}

interface CollapseOptions {
    /** Dates this save rewrote, MM-DD-YYYY */
    rewrittenDates: string[];
    /** Shift name the save applied, e.g. "Night Shift" */
    shiftName: string | undefined;
    /** Dates the new shift is itself block-based on (24/7 keeps its pair) */
    blockBasedDates: Set<string | undefined | null>;
}

/**
 * Drop the spare rows left behind when a date moves off a multi-block shift.
 *
 * Re-saving a 24/7 date onto a single-block shift rewrites BOTH of its rows
 * with the same times, so the date renders twice ("Monday 7th September 19:00 -
 * 10:00" twice over). One row per such date is kept and its block grouping
 * cleared. Rows this save did not write, and dates whose new shift is block-
 * based too, are returned untouched.
 *
 * @param  rows    Visit dates after the save has been applied
 * @param  options Which dates were rewritten, with what, and which stay blocked
 * @return rows with the spares removed
 */
function collapseSingleBlockDates<T extends BlockRowLike>(
    rows: T[],
    options: CollapseOptions,
): T[] {
    const { rewrittenDates, shiftName, blockBasedDates } = options;
    const collapsed = new Set<string>();

    return rows.filter((row) => {
        const date = row.date || '';
        const rewrittenHere = !!row.saved
            && row.shiftName === shiftName
            && rewrittenDates.includes(date);

        if (!rewrittenHere || blockBasedDates.has(date)) {
            return true;
        }
        if (collapsed.has(date)) {
            return false;
        }
        collapsed.add(date);
        return true;
    }).map((row) => (
        row.blockDate && collapsed.has(row.date || '')
            ? { ...row, blockDate: null }
            : row
    ));
}

export default collapseSingleBlockDates;
